import InfiniteScroll from 'react-infinite-scroll-component';

import { Box, BoxProps, CircularProgress, Typography } from '@material-ui/core';

import { ResponseProps } from '@graphql/query/simulationContracts';

import useColorMode from '~/hooks/useColorMode';
import usePortfolio from '~/pages/Portfolio/usePortfolio';

import usePeriods from '../../hooks/usePeriods';
import ContractLabel from '../ContractLabel';
import ContractPeriods from '../ContractPeriods';
import PeriodColumns from '../PeriodColumns';

import { Contracts } from '..';

import useStyles from '../styles';

const isStaging = process.env.REACT_APP_ROLLBAR_ENV === 'staging';

export interface ContractListProps extends BoxProps {
  data: ResponseProps | undefined;
  loading: boolean;
  onLoadMore: () => void;
  handleExpandedPeriods: (period: string) => void;
  operation: string;
  handleExcludedsContracts: (id: string, contractNumber: string) => void;
  excludedContractsTemp: { [id: string]: unknown };
}

const ContractList: React.ComponentType<ContractListProps> = ({
  data,
  loading,
  onLoadMore,
  handleExpandedPeriods,
  operation,
  handleExcludedsContracts,
  excludedContractsTemp,
  ...props
}) => {
  const { bodyInfinitescroll, columnContract, columnFactors } = useStyles();
  const { colorTernary } = useColorMode();
  const { periods } = usePeriods();
  const { operation: _, ...filterValues } = usePortfolio();
  const hasFactors = (filterValues.usageFactorByAngra || filterValues.usageFactor) && operation === 'sale';
  const factorColumns = hasFactors ? Number(Boolean(filterValues.usageFactorByAngra)) + 1 : 0;
  const contractColumnWidth = `${592 + factorColumns * 80}px`;
  const factorsColumnWidth = `${factorColumns * 80}px`;

  if (loading)
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height={isStaging ? 'calc(100% - 146px)' : 'calc(100% - 82px)'}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={isStaging ? 'calc(100vh - 290px)' : 'calc(100vh - 270px)'}
        >
          <CircularProgress size={30} />
        </Box>
      </Box>
    );

  return (
    <Box height={isStaging ? 'calc(100% - 146px)' : 'calc(100% - 82px)'} {...props}>
      <InfiniteScroll
        dataLength={data?.simulationContracts?.length ?? 0}
        hasMore={(data?.simulationContracts?.length ?? 0) % Contracts.LIMIT === 0}
        next={onLoadMore}
        height={isStaging ? 'calc(100vh - 290px)' : 'calc(100vh - 270px)'}
        loader={
          <Box display="flex" alignItems="center" position="sticky" left="0" justifyContent="center" pt="5px" pb="5px">
            <CircularProgress size={30} />
          </Box>
        }
      >
        <Box
          className={bodyInfinitescroll}
          gridTemplateColumns={`${contractColumnWidth} repeat(${periods.length}, 120px) 1fr 20px`}
          zIndex={3}
        >
          <Box className={columnContract} zIndex={2}>
            <Box width="64px" flexShrink={0} />
            <Box display="grid" gridTemplateColumns="72px 110px 24px 32px 130px 130px" gridGap="6px" alignItems="center">
              <Typography variant="body2" color="inherit" style={{ fontWeight: 500 }}>
                Contrato
              </Typography>
              <Typography variant="body2" color="inherit" style={{ fontWeight: 500 }}>
                Modulação
              </Typography>
              <span />
              <span />
              <Typography variant="body2" color="inherit" style={{ fontWeight: 500 }}>
                Vendedor
              </Typography>
              <Typography variant="body2" color="inherit" style={{ fontWeight: 500 }}>
                Comprador
              </Typography>
            </Box>
            {hasFactors && (
              <Box
                className={columnFactors}
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                width={factorsColumnWidth}
                flexShrink={0}
                marginLeft="auto"
              >
                {filterValues.usageFactorByAngra && (
                  <Typography
                    variant="body2"
                    color="inherit"
                    style={{ width: '80px', paddingInline: '10px', textAlign: 'right', fontWeight: 500, boxSizing: 'border-box' }}
                  >
                    C. Angra
                  </Typography>
                )}
                {(filterValues.usageFactorByAngra || filterValues.usageFactor) && (
                  <Typography
                    variant="body2"
                    color="inherit"
                    style={{ width: '80px', paddingInline: '10px', textAlign: 'right', fontWeight: 500, boxSizing: 'border-box' }}
                  >
                    C. Aplicado
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <PeriodColumns periods={periods} handleExpandedPeriods={handleExpandedPeriods} />
        </Box>
        {data?.simulationContracts?.filter(Boolean).map((contract) => (
          <Box key={`${contract.id}-${contract.energyType}`}>
            <Box
              display="grid"
              gridTemplateColumns={`${contractColumnWidth} repeat(${periods.length}, 124px) 1fr 20px`}
              color={contract.id in excludedContractsTemp ? colorTernary('#5c5c5c', '#cbcbcb') : colorTernary('#CCC', '#262626')}
              style={{ width: 'fit-content', alignItems: 'center', justifyContent: 'flex-end', height: '40px' }}
            >
              <ContractLabel
                contract={contract}
                handleExcludedsContracts={handleExcludedsContracts}
                excludedContract={contract.id in excludedContractsTemp}
              />
              <ContractPeriods contract={contract} excludedContract={contract.id in excludedContractsTemp} periods={periods} />
            </Box>
          </Box>
        ))}
      </InfiniteScroll>
    </Box>
  );
};

export default ContractList;
